import { Bullet, PoolTable, PoolTextField } from '@synergy/components';
import { CancelIcon } from '@synergy/theme-next';
import { ChangeEvent } from 'react';
import { FormattedMessage } from 'react-intl';
import { IconButton, styled } from '@mui/material';
import { noop } from '@synergy/utils';

import { DemandAccountItem } from '../../../../../models/demand-account-item';
import { DemandSteps } from '../../../models/demand-steps';
import { demandDetailStepperStateSelector } from '../../../store/demand-detail-selectors';
import { useAppSelector } from '../../../../../../../store/use-appstate-selector';

/** Above and below the text of a row, the same on either side. */
const CELL_PADDING = 14;
/**
 * The height of a field in this table, as it renders.
 *
 * A dense PoolTextField, same as the offer table of the inquiry, where it
 * measures 29px.
 */
const FIELD_HEIGHT = 29;
/** One line of the text in the cells beside it. */
const CELL_TEXT_LINE = 16;

/**
 * What a cell holding a field keeps above it.
 *
 * A field is taller than one line of text and centres its own line inside
 * itself, so its cell hands back the difference. The text in the field then
 * lands on the line of the text beside it, and every cell keeps equal air above
 * and below its content — derived from the two heights instead of measured per
 * cell (#2216, Sammelpunkt Ausrichtung).
 */
const FIELD_CELL_PADDING_TOP = CELL_PADDING + (CELL_TEXT_LINE - FIELD_HEIGHT) / 2;

/** Six digits with a separator. Only the figures are measured; the field takes
 *  whatever its unit needs. */
const FIGURE_WIDTH = 56;

const StyledPoolTextfield = styled(PoolTextField)(() => ({
    // Fills its cell so every field of a column is the same width.
    width: '100%',
    '& > div > input': {
        textAlign: 'right',
        width: FIGURE_WIDTH,
    },
}));

/** The unit inside the field rather than floating above it (#2216). */
const Unit = styled('span')(({ theme }) => ({
    fontSize: 12,
    lineHeight: 1,
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
}));

const StyledPoolTable = styled(PoolTable)(() => ({
    padding: '10px 10px',
    marginBottom: 20,

    /* table head */
    '& thead tr': {
        height: 'auto',
    },
    '& thead tr th': {
        textAlign: 'right',
        padding: '7px 15px 5px 10px',
        margin: 0,
        whiteSpace: 'nowrap',
    },
    '& thead tr th:nth-child(1)': {
        textAlign: 'center',
    },
    '& thead tr th:nth-child(2)': {
        textAlign: 'left',
    },

    /* table body */
    '& tbody tr td': {
        padding: `${CELL_PADDING}px 15px ${CELL_PADDING}px 10px`,
        textAlign: 'right',
        verticalAlign: 'top',
    },
    '& tbody tr td:nth-child(1)': {
        textAlign: 'center',
    },
    '& tbody tr td:nth-child(2)': {
        textAlign: 'left',
    },
    /* the cells that hold a field or a button */
    '& tbody tr td:nth-child(3), & tbody tr td:last-child': {
        paddingTop: FIELD_CELL_PADDING_TOP,
    },
}));

interface FeedbackDetailTableProps {
    items: DemandAccountItem[];
    onQuantityUpdate: (demandAccountItem: DemandAccountItem, quantity: number) => void;
    onDeleteItem: (demandAccountItem: DemandAccountItem) => void;
}

export const FeedbackDetailTable = (props: FeedbackDetailTableProps) => {
    const { items, onQuantityUpdate, onDeleteItem } = props;
    const readonly = useAppSelector(demandDetailStepperStateSelector).readonlySteps.includes(DemandSteps.Feedback);

    const columns = [
        { id: 'pos', label: <FormattedMessage id={'app.table.column.positionNumber'} />, sortable: false },
        {
            id: 'address',
            label: <FormattedMessage id="app.table.column.deliveryAddress" />,
            sortable: false,
            width: '70%',
        },
        { id: 'quantity', label: <FormattedMessage id={'app.table.column.quantity'} />, sortable: false },
        { id: 'delete', sortable: false },
    ];

    const handleQuantityChange = (demandAccountItem: DemandAccountItem) => (event: ChangeEvent<HTMLInputElement>) => {
        if (onQuantityUpdate) {
            onQuantityUpdate(demandAccountItem, parseInt(event.target.value, 10));
        }
    };

    const handleDeleteItem = (demandAccountItem: DemandAccountItem) => {
        onDeleteItem(demandAccountItem);
    };

    const getRow = (demandAccountItem: DemandAccountItem, index: number) => {
        return {
            id: demandAccountItem.id || 0,
            pos: index + 1,
            address: (
                <span>
                    {/* The location's own name first: a member with three
                        delivery points tells them apart by that, not by the
                        street (#2215, R-75). */}
                    {demandAccountItem.deliveryAddress?.title && (
                        <>
                            {demandAccountItem.deliveryAddress.title}
                            <Bullet />
                        </>
                    )}
                    {demandAccountItem.deliveryAddress?.street} {demandAccountItem.deliveryAddress?.number}
                    <Bullet />
                    {demandAccountItem.deliveryAddress?.zip} {demandAccountItem.deliveryAddress?.city}
                </span>
            ),
            quantity: (
                <StyledPoolTextfield
                    id="quantity"
                    name="quantity"
                    fieldType="dense"
                    endAdornment={<Unit title={demandAccountItem.orderUnit}>{demandAccountItem.orderUnit}</Unit>}
                    isNumeric={true}
                    value={demandAccountItem.quantity}
                    onChange={handleQuantityChange(demandAccountItem)}
                    disabled={readonly}
                />
            ),
            delete: (
                <IconButton color="primary" onClick={() => handleDeleteItem(demandAccountItem)}>
                    <CancelIcon />
                </IconButton>
            ),
        };
    };

    return (
        <div>
            <StyledPoolTable
                enableHover={false}
                columns={columns}
                rows={items.map((item, index) => getRow(item, index))}
                onRowClicked={noop}
            />
        </div>
    );
};
